import { Archive, ArchiveRestore, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useUpdateSessionByIdMutation } from '~entities/session/api/update-session-by-id';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

interface ArchiveSessionButtonProps {
  id: string;
  isArchived: boolean;
}

export const ArchiveSessionButton = ({
  id,
  isArchived,
}: ArchiveSessionButtonProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const { mutateAsync: updateSession, isPending } =
    useUpdateSessionByIdMutation();

  const handleArchive = async () => {
    try {
      await updateSession({
        id,
        isArchive: !isArchived,
      });
      toast.success(
        `Session ${isArchived ? 'unarchived' : 'archived'} successfully`,
      );
      setShowDialog(false);
    } catch (error) {
      console.error('🚀 ~ handleArchive ~ error:', error);
      toast.error(`Failed to ${isArchived ? 'unarchive' : 'archive'} session`);
    }
  };

  return (
    <>
      <Button
        variant={'secondary'}
        className='size-6 p-0'
        onClick={() => {
          setShowDialog(true);
        }}
      >
        {isArchived ? (
          <ArchiveRestore className='size-6' />
        ) : (
          <Archive className='size-6' />
        )}
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchived ? 'Unarchive Session' : 'Archive Session'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {isArchived ? 'unarchive' : 'archive'}{' '}
              this session?{' '}
              {isArchived
                ? 'It will be restored and visible again.'
                : 'It will be hidden from the active sessions list.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <Button onClick={handleArchive} disabled={isPending}>
              {isPending && <Loader2 className='animate-spin' />}
              {isArchived ? 'Unarchive' : 'Archive'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
